import { K8sProviderIcon } from '@cast/features-cluster-ui';
import { ChangeEvent, useRef, useState } from 'react';

import { FormControlLabel } from '@mui/material';
import { useModal } from 'mui-modal-provider';

import { CAST_AI } from '@cast/constants';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Divider,
  Icons,
  Input,
  ShowModalOutput,
  CloseDialogButton,
  Typography,
  Box,
  Stack,
  colorOrToken,
  tokens,
} from '@cast/design-system';
import { Cluster } from '@cast/types';

import { mapClusterToCloud } from 'utils/csp';

import { useDisconnectClusterMutation } from '../../../mutations';

import { getDocsLink } from '@cast/utils';

type Props = DialogProps & {
  onClose: () => void;
  cluster: Cluster;
};

const DisconnectPhase2ClusterDialog = ({
  cluster,
  onClose,
  ...props
}: Props) => {
  const [name, setName] = useState('');
  const [deleteProvisionedNodes, setDeleteProvisionedNodes] = useState(false);
  const { mutate, isPending } = useDisconnectClusterMutation(cluster.id);
  const cloud = mapClusterToCloud(cluster);

  const handleChange = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setName(evt.target.value);
  };

  const handleChecked = (event: ChangeEvent<HTMLInputElement>) => {
    setDeleteProvisionedNodes(event.target.checked);
  };

  const handleDelete = () => {
    mutate({ deleteProvisionedNodes });
    setDeleteProvisionedNodes(false);
    onClose();
  };

  const handleClose = () => {
    setName('');
    setDeleteProvisionedNodes(false);
    onClose();
  };

  return (
    <Dialog {...props} onClose={handleClose} testId="phase2-disconnect-dialog">
      <DialogTitle sx={{ p: '16px 32px' }}>
        <Stack gap={24}>
          <Stack direction="row">
            <Stack direction="row" gap={4}>
              <K8sProviderIcon provider={cluster.providerType} size={24} />
              <Typography
                variant="A1"
                color={tokens.theme.fg.structural.primary}
              >
                {cluster.name}
              </Typography>
            </Stack>

            <CloseDialogButton onClose={onClose} />
          </Stack>

          <Stack gap={8}>
            <Typography variant="h4" color={tokens.theme.fg.structural.primary}>
              Disconnect your cluster
            </Typography>
            <Typography
              variant="P14R"
              color={tokens.theme.fg.structural.secondary}
            >
              This action will remove all {CAST_AI} resources managing your
              cluster. {CAST_AI} user can’t be deleted automatically, please go
              to {cloud?.toUpperCase()} IAM and delete it manually.{' '}
              <Button
                variant="text"
                component="a"
                href={getDocsLink('docs_remove_resource')}
                target="_blank"
                rel="noreferrer noopener"
                sx={{ typography: 'P14M' }}
              >
                Full list of resources
              </Button>
            </Typography>
          </Stack>
        </Stack>
      </DialogTitle>
      <Divider color={tokens.theme.border.structural.neutralSubtle} />
      <DialogContent sx={{ p: '16px 32px 24px' }}>
        <Stack gap={8}>
          <Stack gap={6}>
            <Typography variant="A1" color={tokens.theme.fg.structural.primary}>
              Confirmation
            </Typography>
            <Typography
              variant="P12R"
              color={tokens.theme.fg.structural.secondary}
            >
              Please confirm that you want to disconnect from {CAST_AI} by
              entering the cluster name below.
            </Typography>
          </Stack>

          <Input
            testId="cluster-name-input"
            disabled
            fullWidth
            value={cluster.name}
            inputProps={{ 'aria-label': 'cluster name display' }}
          />

          <Input
            testId="repeat-cluster-name-input"
            autoFocus
            onChange={handleChange}
            placeholder="Enter the cluster name"
            inputProps={{ 'aria-label': 'cluster name input' }}
            id="name"
            type="text"
            fullWidth
            hiddenLabel
          />
        </Stack>
      </DialogContent>
      <Box bgcolor={colorOrToken('blue.50', tokens.theme.bg.status.infoSubtle)}>
        <DialogContent sx={{ p: '16px 32px 20px' }}>
          <FormControlLabel
            sx={{ display: 'flex', alignItems: 'baseline', gap: 8, m: 0 }}
            control={
              <Checkbox
                testId="delete-castai-nodes-checkbox"
                size="small"
                checked={deleteProvisionedNodes}
                onChange={handleChecked}
              />
            }
            label={
              <Stack gap={4}>
                <Stack direction="row" gap={8}>
                  <Typography
                    variant="A1"
                    color={tokens.theme.fg.structural.primary}
                  >
                    Delete all {CAST_AI} created nodes
                  </Typography>

                  <Chip
                    size="sm"
                    startIcon={<Icons.Warning />}
                    sx={{
                      backgroundColor: colorOrToken(
                        'yellow.700',
                        tokens.theme.bg.status.warningStrong
                      ),
                      color: colorOrToken(
                        '#fff',
                        tokens.theme.fg.status.onWarningStrong
                      ),
                    }}
                  >
                    Might cause downtime
                  </Chip>
                </Stack>

                <Typography
                  variant="P12R"
                  color={tokens.theme.fg.structural.secondary}
                >
                  All {CAST_AI} created nodes will be deleted. Depending on your
                  application configuration this action might cause downtime.
                </Typography>
              </Stack>
            }
          />
        </DialogContent>
      </Box>
      <DialogActions sx={{ p: '16px 32px' }}>
        <Stack direction="row" justifyContent="flex-end" gap={16}>
          <Button
            variant="secondary"
            onClick={handleClose}
            testId="close-dialog-action"
          >
            Cancel
          </Button>

          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={name !== cluster.name}
            loading={isPending}
            testId="disconnect-cluster-action"
          >
            Disconnect
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export const useDisconnectPhase2ClusterDialog = () => {
  const { showModal } = useModal();
  const modalInstance = useRef<ShowModalOutput>(undefined);

  return {
    openDisconnectPhase2ClusterDialog: (cluster: Cluster) => {
      modalInstance.current = showModal(
        DisconnectPhase2ClusterDialog,
        {
          cluster,
          onClose: () => modalInstance.current?.destroy(),
        },
        { rootId: 'root' }
      );
    },
  };
};
